import React, { useState } from 'react';
import { OnboardingStep } from '../../../contexts/OnboardingContext';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { 
  UserIcon, 
  BriefcaseIcon, 
  CodeBracketIcon,
  BuildingOfficeIcon 
} from '@heroicons/react/24/outline';

interface UserProfileStepProps {
  step: OnboardingStep;
}

interface UserProfile {
  name: string;
  email: string;
  role: 'developer' | 'admin' | 'business' | '';
  experience: 'beginner' | 'intermediate' | 'advanced' | '';
  interests: string[];
  company?: string;
}

export function UserProfileStep({ step }: UserProfileStepProps) {
  const { completeStep } = useOnboarding();
  const [profile, setProfile] = useState<UserProfile>({
    name: '',
    email: '',
    role: '',
    experience: '',
    interests: [],
    company: ''
  });

  const roles = [
    {
      id: 'developer' as const,
      title: 'Developer',
      description: 'Building apps and integrations',
      icon: CodeBracketIcon
    },
    {
      id: 'admin' as const,
      title: 'IT Admin',
      description: 'Managing systems and workflows',
      icon: BuildingOfficeIcon
    },
    {
      id: 'business' as const,
      title: 'Business User',
      description: 'Connecting business tools',
      icon: BriefcaseIcon
    }
  ];

  const experiences = [
    { id: 'beginner' as const, label: 'New to integrations' },
    { id: 'intermediate' as const, label: 'Some experience' },
    { id: 'advanced' as const, label: 'Very experienced' }
  ];

  const availableInterests = [
    'Email automation', 'CRM integration', 'Data syncing', 
    'Workflow automation', 'API development', 'Cloud platforms',
    'Marketing tools', 'Project management', 'Analytics',
    'Social media', 'E-commerce', 'Customer support'
  ];

  const handleInterestToggle = (interest: string) => {
    setProfile(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleComplete = () => {
    // Save profile data (in real app, this would persist to backend)
    localStorage.setItem('userProfile', JSON.stringify(profile));
    completeStep(step.id);
  };

  const isValid = profile.name && profile.email && profile.role && profile.experience;

  return (
    <div>
      <div className="text-center mb-6">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900">
          <UserIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h3 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">
          {step.title}
        </h3>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          {step.description}
        </p>
      </div>

      <div className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="Your name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="you@example.com"
            />
          </div>
        </div>

        {/* Company */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Company (Optional)
          </label>
          <input
            type="text"
            value={profile.company}
            onChange={(e) => setProfile(prev => ({ ...prev, company: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
            placeholder="Your company"
          />
        </div>

        {/* Role Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            What's your role? *
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {roles.map((role) => (
              <button
                key={role.id}
                type="button"
                onClick={() => setProfile(prev => ({ ...prev, role: role.id }))}
                className={`relative flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                  profile.role === role.id
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <role.icon className={`h-8 w-8 mb-2 ${
                  profile.role === role.id 
                    ? 'text-indigo-600 dark:text-indigo-400' 
                    : 'text-gray-400'
                }`} />
                <div className={`font-medium text-sm ${
                  profile.role === role.id 
                    ? 'text-indigo-900 dark:text-indigo-100' 
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {role.title}
                </div>
                <div className={`text-xs text-center mt-1 ${
                  profile.role === role.id 
                    ? 'text-indigo-700 dark:text-indigo-300' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {role.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Experience Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Experience with integrations? *
          </label>
          <div className="space-y-2">
            {experiences.map((exp) => (
              <label key={exp.id} className="flex items-center">
                <input
                  type="radio"
                  name="experience"
                  value={exp.id}
                  checked={profile.experience === exp.id}
                  onChange={(e) => setProfile(prev => ({ ...prev, experience: e.target.value as any }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {exp.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Interests */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            What are you interested in? (Select all that apply)
          </label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {availableInterests.map((interest) => (
              <button
                key={interest}
                type="button"
                onClick={() => handleInterestToggle(interest)}
                className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                  profile.interests.includes(interest)
                    ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300'
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {interest}
              </button>
            ))}
          </div>
        </div>

        {/* Personalized recommendations */}
        {profile.role && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
              💡 Recommended for {roles.find(r => r.id === profile.role)?.title}s
            </h4>
            <div className="text-sm text-blue-800 dark:text-blue-200">
              {profile.role === 'developer' && (
                <ul className="list-disc list-inside space-y-1">
                  <li>GitHub integration for code management</li>
                  <li>Slack for team notifications</li>
                  <li>CI/CD tools like Jenkins or GitHub Actions</li>
                </ul>
              )}
              {profile.role === 'admin' && (
                <ul className="list-disc list-inside space-y-1">
                  <li>Google Workspace for user management</li>
                  <li>Microsoft Teams for communication</li>
                  <li>Monitoring tools like DataDog or New Relic</li>
                </ul>
              )}
              {profile.role === 'business' && (
                <ul className="list-disc list-inside space-y-1">
                  <li>CRM tools like Salesforce or HubSpot</li>
                  <li>Email marketing platforms</li>
                  <li>Project management tools like Asana</li>
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Continue Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleComplete}
          disabled={!isValid}
          className={`px-4 py-2 rounded-md font-medium ${
            isValid
              ? 'bg-indigo-600 text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500'
              : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}